import { LineSeries } from 'lightweight-charts';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { IndicatorType } from '@shared/types';
import { useChartContext } from '../components/chart.context';
import { useChartStreamContext } from '../components/chart-stream.context';
import {
  serializeNumericTime,
  toSortableTime
} from './utils/time-normalization.utils';
import { useIndicatorUpdateStream } from './use-indicator-update-stream.hook';
import {
  isZigZagIndicatorUpdate,
  ZigZagIndicatorUpdate,
  ZigZagIndicatorUpdatePoint
} from '../services/indicator-update.service';

type ZigZagPointCache = Map<string, ZigZagIndicatorUpdatePoint>;

const toZigZagLineData = (points: ZigZagIndicatorUpdate['data']) =>
  points.flatMap((point) => {
    const time = point.swingEnd?.time;
    const value = point.swingEnd?.price;

    if (time == null || typeof value !== 'number') return [];

    return [{ time, value }];
  });

export const useZigZagSeries = () => {
  const {
    chartRef,
    overlayIndicatorSeriesRefs: indicatorSeriesRefs,
    chartInitialized,
    data: { indicatorConfigs }
  } = useChartContext();
  const { timeframeIndex } = useChartStreamContext();

  // Cache points per indicatorIndex; multiple ZigZag configs can coexist on one chart.
  const indicatorDataCacheRef = useRef<Map<number, ZigZagPointCache>>(
    new Map()
  );

  const hasZigZag = useMemo(
    () =>
      indicatorConfigs.overlay.some(
        (indicator) => indicator.type === IndicatorType.ZIG_ZAG
      ),
    [indicatorConfigs.overlay]
  );

  const getOrCreateSeries = useCallback((indicatorIndex: number) => {
    if (!chartRef.current) return null;

    if (!indicatorSeriesRefs.current[IndicatorType.ZIG_ZAG]) {
      indicatorSeriesRefs.current[IndicatorType.ZIG_ZAG] = [];
    }

    const zigZagSeries = indicatorSeriesRefs.current[IndicatorType.ZIG_ZAG];
    if (!zigZagSeries) return null;

    if (!zigZagSeries[indicatorIndex]) {
      zigZagSeries[indicatorIndex] = chartRef.current.addSeries(LineSeries, {
        color: 'white',
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false
      });
    }

    return zigZagSeries[indicatorIndex] ?? null;
  }, []);

  const mergeIndicatorData = useCallback(
    (
      indicatorIndex: number,
      points: ZigZagIndicatorUpdate['data'],
      replace: boolean
    ) => {
      // ZigZag can repaint prior pivots (not only append), and deltas can be partial.
      // Cache by time so incoming points overwrite existing pivots, then redraw full line.
      const nextCache: ZigZagPointCache = replace
        ? new Map()
        : new Map(indicatorDataCacheRef.current.get(indicatorIndex) ?? []);

      points.forEach((point) => {
        nextCache.set(serializeNumericTime(point.time, 'n'), point);
      });

      indicatorDataCacheRef.current.set(indicatorIndex, nextCache);
      // setData requires monotonically increasing times, so normalize order after merges.
      return Array.from(nextCache.values()).sort(
        (left, right) => toSortableTime(left.time) - toSortableTime(right.time)
      );
    },
    []
  );

  const applyUpdate = useCallback(
    (update: ZigZagIndicatorUpdate) => {
      const series = getOrCreateSeries(update.indicatorIndex);
      if (!series) return;

      const mergedIndicatorData = mergeIndicatorData(
        update.indicatorIndex,
        update.data,
        update.updateType === 'snapshot'
      );

      // Use setData instead of update: repaint can change older points, not just the tail.
      series.setData(toZigZagLineData(mergedIndicatorData));
    },
    [getOrCreateSeries, mergeIndicatorData]
  );

  useEffect(() => {
    // Timeframe switch invalidates cached ZigZag points from previous dataset.
    indicatorDataCacheRef.current.clear();
  }, [timeframeIndex]);

  useIndicatorUpdateStream({
    chartInitialized,
    enabled: hasZigZag,
    indicatorType: IndicatorType.ZIG_ZAG,
    isExpectedUpdate: isZigZagIndicatorUpdate,
    applyUpdate
  });
};
